// Reine Darstellungsregeln der Hausmeister-App (Portale-Redesign E2). Keine Datenzugriffe –
// Laden und Speichern bleiben unverändert in lib/hausmeister/api.ts und lib/zeiterfassung/api.ts.
import type { HausmeisterTicket, Material } from './api';

/** Die drei Stufen, die der Hausmeister setzt und sieht. */
export type StatusStufe = 'offen' | 'in_bearbeitung' | 'erledigt';

export const STATUS_STUFEN: { key: StatusStufe; label: string }[] = [
  { key: 'offen', label: 'Offen' },
  { key: 'in_bearbeitung', label: 'In Arbeit' },
  { key: 'erledigt', label: 'Erledigt' },
];

/**
 * Ticket-Status (Verwaltung kennt mehr Werte) → Stufe für die Anzeige.
 * erledigt/geschlossen = Erledigt, in_bearbeitung = In Arbeit, alles andere = Offen.
 */
export function statusStufe(status: string | null | undefined): StatusStufe {
  if (status === 'erledigt' || status === 'geschlossen') return 'erledigt';
  if (status === 'in_bearbeitung') return 'in_bearbeitung';
  return 'offen';
}

export const stufenLabel = (s: StatusStufe): string => STATUS_STUFEN.find((x) => x.key === s)?.label ?? 'Offen';

export function zaehleStufen(tickets: Pick<HausmeisterTicket, 'status'>[]): Record<StatusStufe, number> {
  const z: Record<StatusStufe, number> = { offen: 0, in_bearbeitung: 0, erledigt: 0 };
  for (const t of tickets) z[statusStufe(t.status)] += 1;
  return z;
}

/** Nicht erledigte Aufträge (Badge am Tab „Aufträge“). */
export const offeneAnzahl = (tickets: Pick<HausmeisterTicket, 'status'>[]): number =>
  tickets.filter((t) => statusStufe(t.status) !== 'erledigt').length;

export function adresse(t: Pick<HausmeisterTicket, 'objekt'>): string {
  const o = t.objekt;
  if (!o) return '';
  return [[o.strasse, o.hausnummer].filter(Boolean).join(' '), [o.plz, o.stadt].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ');
}

export const mapsLink = (adr: string): string => `https://maps.google.com/?q=${encodeURIComponent(adr)}`;

/** „Als Nächstes“: bis zu drei offene Aufträge ohne den, auf dem gerade die Zeit läuft. */
export function alsNaechstes<T extends Pick<HausmeisterTicket, 'id' | 'status'>>(tickets: T[], laufendeTicketId: string | null): T[] {
  return tickets.filter((t) => statusStufe(t.status) !== 'erledigt' && t.id !== laufendeTicketId).slice(0, 3);
}

export function gruss(jetzt: Date, vollerName: string | null | undefined): string {
  const h = jetzt.getHours();
  const anrede = h < 11 ? 'Guten Morgen' : h < 18 ? 'Hallo' : 'Guten Abend';
  const vorname = (vollerName ?? '').trim().split(/\s+/)[0];
  return vorname ? `${anrede}, ${vorname}` : anrede;
}

export const datumLang = (d: Date): string =>
  d.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });

export const uhrzeit = (iso: string | Date): string =>
  new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

export const notizZeit = (iso: string): string =>
  new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

export const auftragAnzahlText = (n: number): string => (n === 1 ? '1 Auftrag' : `${n} Aufträge`);

export const euro = (n: number): string => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

export const kmText = (n: number): string => `${n.toLocaleString('de-DE')} km`;

/** Summe der Positionen mit Preis (Menge × Einzelpreis). */
export function materialSumme(material: Pick<Material, 'menge' | 'einzelpreis'>[]): number {
  return material.reduce((s, m) => s + (m.einzelpreis !== null && m.einzelpreis !== undefined ? Number(m.menge) * Number(m.einzelpreis) : 0), 0);
}

/**
 * Eingaben „Material erfassen“ – Regeln wie bisher: ohne Bezeichnung nichts speichern,
 * Komma als Dezimaltrenner, Menge ohne gültige Zahl = 1, Preis nur, wenn er eine Zahl ist.
 */
export function materialEingabe(menge: string, bezeichnung: string, preis: string): { bezeichnung: string; menge: number; einzelpreis: number | null } | null {
  if (!bezeichnung.trim()) return null;
  const p = preis.trim() ? Number(preis.replace(',', '.')) : null;
  return {
    bezeichnung,
    menge: Number(menge.replace(',', '.')) || 1,
    einzelpreis: p !== null && Number.isFinite(p) ? p : null,
  };
}

/** Kilometer-Eingabe: Komma erlaubt, nur Werte > 0 zählen (wie bisher). */
export function kmWert(text: string): number | null {
  const w = Number(text.replace(',', '.'));
  return Number.isFinite(w) && w > 0 ? w : null;
}

const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

export interface WochenBalken {
  label: string;
  minuten: number;
  hoehe: number;
  heute: boolean;
  tip: string;
}

/** Stunden:Minuten ohne Einheit, abgerundet (Anzeige wie im Prototyp, z. B. „19:01“). */
export function hm(minuten: number): string {
  const m = Math.max(0, Math.floor(minuten));
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
}

/** Wochenbalken Mo–So aus ZeitContext.woche; Höhe relativ zum größten Tag (mind. 9 h). */
export function wochenBalken(woche: { tag: string; istMinuten: number }[], heute: string): WochenBalken[] {
  const max = Math.max(540, ...woche.map((w) => w.istMinuten));
  return woche.map((w, i) => ({
    label: WOCHENTAGE[i] ?? '',
    minuten: w.istMinuten,
    hoehe: Math.max(6, Math.round((w.istMinuten / max) * 70)),
    heute: w.tag === heute,
    tip: `${WOCHENTAGE[i] ?? ''}: ${hm(w.istMinuten)} h`,
  }));
}

/** Erster und letzter Tag (YYYY-MM-DD) der Woche aus ZeitContext.woche. */
export function wochenGrenzen(woche: { tag: string }[]): { von: string; bis: string } | null {
  if (!woche.length) return null;
  return { von: woche[0].tag, bis: woche[woche.length - 1].tag };
}
